import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Mail,
  MessageSquare,
  Bell,
  Slack,
  Filter,
  Download,
  X,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Eye,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";

const CHANNELS = [
  { value: "email", label: "Email", icon: Mail },
  { value: "in_app", label: "In-app", icon: Bell },
  { value: "slack", label: "Slack", icon: Slack },
  { value: "teams", label: "Teams", icon: MessageSquare },
  { value: "sms", label: "SMS", icon: MessageSquare },
];
const STATUSES = [
  { value: "sent", label: "Sent", colour: "bg-success-50 text-success-700 border-success-200" },
  { value: "mock_send", label: "Mock", colour: "bg-warning-50 text-warning-700 border-warning-200" },
  { value: "failed", label: "Failed", colour: "bg-danger-50 text-danger-700 border-danger-200" },
];
const KIND_OPTIONS = [
  "host_arrival", "weekly_digest", "erasure_confirm_link",
  "sar_link", "pre_reg_invitation", "insurance_expiring",
  "insurance_expired", "watchlist_hit_admin", "user_invitation",
  "verify_email", "password_reset", "bulk_rams_link",
];
const DATE_PRESETS = [
  { value: "24h", label: "Last 24h", hours: 24 },
  { value: "7d", label: "Last 7 days", hours: 7 * 24 },
  { value: "30d", label: "Last 30 days", hours: 30 * 24 },
  { value: "all", label: "All time", hours: null },
];

const channelIcon = (channel) => {
  const c = CHANNELS.find((x) => x.value === channel);
  return c ? c.icon : Mail;
};
const statusBadge = (status) => STATUSES.find((s) => s.value === status) || { label: status, colour: "bg-navy-50 text-navy-600 border-navy-200" };

export default function NotificationsLog() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [orgIdFilter, setOrgIdFilter] = useState("");
  const [recipientQ, setRecipientQ] = useState("");
  const recipientTimer = useRef(null);
  const [channels, setChannels] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [datePreset, setDatePreset] = useState("7d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const dateRange = useMemo(() => {
    if (datePreset === "custom") return { date_from: customFrom, date_to: customTo };
    const preset = DATE_PRESETS.find((p) => p.value === datePreset);
    if (!preset || preset.hours == null) return {};
    const from = new Date(Date.now() - preset.hours * 60 * 60 * 1000).toISOString();
    return { date_from: from };
  }, [datePreset, customFrom, customTo]);

  const queryParams = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE, ...dateRange };
    if (channels.length) p.channel = channels.join(",");
    if (statuses.length) p.status = statuses.join(",");
    if (kinds.length) p.kind = kinds.join(",");
    if (recipientQ.trim()) p.recipient = recipientQ.trim();
    if (isSuper && orgIdFilter.trim()) p.org_id = orgIdFilter.trim();
    return p;
  }, [page, dateRange, channels, statuses, kinds, recipientQ, isSuper, orgIdFilter]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications-log", { params: queryParams });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { refresh(); }, [refresh]);

  const onRecipientChange = (v) => {
    setRecipientQ(v);
    if (recipientTimer.current) clearTimeout(recipientTimer.current);
    recipientTimer.current = setTimeout(() => setPage(1), 350);
  };

  const toggleArrayValue = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(queryParams).forEach(([k, v]) => {
        if (k !== "page" && k !== "page_size" && v != null && v !== "") params.append(k, v);
      });
      const url = `/notifications-log/export.csv?${params.toString()}`;
      const resp = await api.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `entrybook-notifications-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("CSV downloaded");
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 413) {
        toast.error(det?.message || "Result set too large — narrow date range and retry");
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <SettingsLayout>
      <div className="space-y-5" data-testid="notifications-log-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eb-overline mb-1">Module 6.8</div>
            <h1 className="text-3xl font-bold text-navy-900">Notifications log</h1>
            <p className="text-sm text-navy-500 mt-1">
              Every email, in-app, Slack, Teams, and SMS event for {isSuper ? "any" : "your"} org.
              {isSuper && " Filter by org_id to inspect cross-org delivery."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={refresh} disabled={loading} data-testid="nl-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={exportCsv} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="nl-export-csv">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        <div className="eb-card p-5">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-navy-700">
            <Filter className="w-4 h-4" /> Filters
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="eb-label">Date range</label>
              <select className="eb-input" value={datePreset} onChange={(e) => { setDatePreset(e.target.value); setPage(1); }}
                       data-testid="nl-date-preset">
                {DATE_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                <option value="custom">Custom…</option>
              </select>
              {datePreset === "custom" && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <input type="datetime-local" className="eb-input" onChange={(e) => setCustomFrom(e.target.value ? new Date(e.target.value).toISOString() : "")} />
                  <input type="datetime-local" className="eb-input" onChange={(e) => setCustomTo(e.target.value ? new Date(e.target.value).toISOString() : "")} />
                </div>
              )}
            </div>
            <div>
              <label className="eb-label">Recipient (substring)</label>
              <input className="eb-input" value={recipientQ} onChange={(e) => onRecipientChange(e.target.value)}
                     placeholder="e.g. olivia" data-testid="nl-recipient-input" />
            </div>
            {isSuper && (
              <div>
                <label className="eb-label">Org ID (super_admin only)</label>
                <input className="eb-input" value={orgIdFilter} onChange={(e) => { setOrgIdFilter(e.target.value); setPage(1); }}
                       placeholder="Leave blank for own org" data-testid="nl-org-id" />
              </div>
            )}
            <div>
              <label className="eb-label">Channels</label>
              <div className="flex flex-wrap gap-1.5">
                {CHANNELS.map((c) => {
                  const Icon = c.icon;
                  const on = channels.includes(c.value);
                  return (
                    <button key={c.value} type="button"
                            onClick={() => { setChannels(toggleArrayValue(channels, c.value)); setPage(1); }}
                            data-testid={`nl-channel-${c.value}`}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border transition-colors ${
                              on ? "bg-orange-500 text-white border-orange-500" : "bg-white text-navy-700 border-navy-200 hover:border-orange-300"
                            }`}>
                      <Icon className="w-3 h-3" /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="eb-label">Status</label>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map((s) => {
                  const on = statuses.includes(s.value);
                  return (
                    <button key={s.value} type="button"
                            onClick={() => { setStatuses(toggleArrayValue(statuses, s.value)); setPage(1); }}
                            data-testid={`nl-status-${s.value}`}
                            className={`px-2 py-1 rounded-md text-xs border transition-colors ${
                              on ? "bg-orange-500 text-white border-orange-500" : `${s.colour} hover:border-orange-300`
                            }`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className="eb-label">Kind</label>
              <div className="flex flex-wrap gap-1.5">
                {KIND_OPTIONS.map((k) => {
                  const on = kinds.includes(k);
                  return (
                    <button key={k} type="button"
                            onClick={() => { setKinds(toggleArrayValue(kinds, k)); setPage(1); }}
                            data-testid={`nl-kind-${k}`}
                            className={`px-2 py-1 rounded-md text-xs border transition-colors ${
                              on ? "bg-orange-500 text-white border-orange-500" : "bg-white text-navy-700 border-navy-200 hover:border-orange-300"
                            }`}>
                      {k}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="eb-card overflow-hidden" data-testid="nl-table">
          <div className="px-5 py-3 border-b border-navy-100 flex items-center justify-between">
            <div className="text-sm text-navy-600">
              <strong>{total.toLocaleString()}</strong> rows · page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}
                      className="p-1.5 rounded hover:bg-navy-50 disabled:opacity-30" data-testid="nl-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}
                      className="p-1.5 rounded hover:bg-navy-50 disabled:opacity-30" data-testid="nl-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-navy-50 text-navy-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Sent at</th>
                <th className="text-left px-4 py-2">Channel</th>
                <th className="text-left px-4 py-2">Kind</th>
                <th className="text-left px-4 py-2">Recipient</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Subject</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Mode</th>
                <th className="px-4 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-navy-400">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-navy-400">No matching rows.</td></tr>
              )}
              {items.map((r) => {
                const Icon = channelIcon(r.channel);
                const sb = statusBadge(r.status);
                return (
                  <tr key={r.id} className="border-t border-navy-100 hover:bg-orange-50/30 cursor-pointer transition-colors"
                      onClick={() => setSelected(r)} data-testid={`nl-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono text-xs text-navy-500 whitespace-nowrap">
                      {(r.sent_at || r.created_at || "").replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1 text-navy-700"><Icon className="w-4 h-4" /> {r.channel}</span>
                    </td>
                    <td className="px-4 py-2 text-navy-700">{r.kind || "—"}</td>
                    <td className="px-4 py-2 text-navy-900 max-w-[14rem] truncate" title={r.recipient}>
                      {(r.recipient || "").length > 30 ? `${r.recipient.slice(0, 30)}…` : (r.recipient || "—")}
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell max-w-[18rem] truncate text-navy-700" title={r.subject || ""}>
                      {r.channel === "email" ? (r.subject || "—") : <span className="text-navy-400">n/a</span>}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${sb.colour}`}>{sb.label}</span>
                    </td>
                    <td className="px-4 py-2">
                      {r.mode === "mock"
                        ? <span className="px-2 py-0.5 rounded-full text-xs bg-warning-50 text-warning-700 border border-warning-200">mock</span>
                        : <span className="px-2 py-0.5 rounded-full text-xs bg-navy-50 text-navy-600 border border-navy-200">live</span>}
                    </td>
                    <td className="px-4 py-2"><Eye className="w-4 h-4 text-navy-400" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selected && <DetailDrawer row={selected} onClose={() => setSelected(null)} isSuper={isSuper} orgIdFilter={orgIdFilter} />}
      </div>
    </SettingsLayout>
  );
}

function DetailDrawer({ row, onClose, isSuper, orgIdFilter }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = (isSuper && orgIdFilter) ? { org_id: orgIdFilter } : {};
    api.get(`/notifications-log/${row.id}`, { params })
      .then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [row.id, isSuper, orgIdFilter]);

  // The strict CSP doc is built client-side and rendered into a sandboxed iframe
  // (sandbox="" — no allow-scripts, no allow-forms, no allow-top-navigation).
  // Using a Blob URL avoids srcdoc and avoids cross-origin auth headache.
  const blobUrl = useMemo(() => {
    if (detail?.channel !== "email") return null;
    const body = detail.body_html || "<p style='color:#888;font-family:sans-serif'>(empty body)</p>";
    const doc =
      "<!DOCTYPE html><html><head><meta charset='utf-8'>" +
      "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'\">" +
      "<style>body{font-family:system-ui,-apple-system,sans-serif;margin:16px;color:#1f2937}</style>" +
      "</head><body>" + body + "</body></html>";
    const blob = new Blob([doc], { type: "text/html" });
    return URL.createObjectURL(blob);
  }, [detail]);

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const sb = statusBadge(row.status);

  return (
    <div className="fixed inset-0 z-40 bg-navy-900/40 backdrop-blur-sm" onClick={onClose} data-testid="nl-drawer">
      <div className="absolute right-0 top-0 h-full w-full sm:w-[640px] bg-white shadow-xl overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-navy-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${sb.colour}`}>{sb.label}</span>
            <span className="text-xs uppercase text-navy-500 tracking-wider">{row.channel} · {row.kind || "—"}</span>
          </div>
          <button onClick={onClose} className="text-navy-400 hover:text-navy-700" data-testid="nl-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && <div className="px-5 py-12 text-center text-navy-400">Loading…</div>}
        {!loading && detail && (
          <div className="p-5 space-y-5 text-sm">
            <Field label="Recipient" value={detail.recipient} />
            <Field label="Subject" value={detail.subject} />
            <Field label="Sent at" value={detail.sent_at || detail.created_at} mono />
            {detail.latency_ms != null && <Field label="Latency" value={`${detail.latency_ms} ms`} />}
            {detail.error_msg && (
              <div className="rounded-lg bg-danger-50 border border-danger-200 p-3 flex gap-2">
                <AlertTriangle className="w-4 h-4 text-danger-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-danger-700">Delivery error</div>
                  <div className="text-xs text-danger-700 mt-0.5 font-mono break-all">{detail.error_msg}</div>
                </div>
              </div>
            )}

            {detail.channel === "email" && blobUrl && (
              <div>
                <div className="eb-overline mb-2">Email body (sandboxed)</div>
                <iframe
                  src={blobUrl}
                  sandbox=""
                  title="email-body-sandboxed"
                  data-testid="nl-email-iframe"
                  className="w-full h-[280px] rounded-lg border border-navy-100 bg-white"
                />
                <p className="text-xs text-navy-400 mt-1">
                  Rendered inside <code>iframe sandbox=&quot;&quot;</code> (no scripts, no forms, no top navigation) with strict CSP.
                </p>
              </div>
            )}

            {detail.channel !== "email" && (
              <div>
                <div className="eb-overline mb-2">Payload</div>
                <pre className="rounded-lg bg-navy-50 text-navy-800 text-xs p-3 overflow-auto max-h-[280px] border border-navy-100"
                     data-testid="nl-payload">{JSON.stringify(detail.payload || {}, null, 2)}</pre>
                {(detail.payload?.kind || detail.kind || "").startsWith?.("watchlist") && (
                  <p className="text-xs text-warning-700 mt-1 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> PII redacted in this payload (watchlist contract).
                  </p>
                )}
              </div>
            )}

            {detail.retry_history?.length > 0 && (
              <div>
                <div className="eb-overline mb-2">Retry history</div>
                <ul className="space-y-1.5">
                  {detail.retry_history.map((h) => (
                    <li key={h.attempt} className="text-xs flex items-center gap-2 text-navy-600">
                      <Clock3 className="w-3 h-3" /> attempt {h.attempt} — {h.status}
                      {h.error && <span className="text-danger-600">— {h.error}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.audit_links?.length > 0 && (
              <div>
                <div className="eb-overline mb-2">Linked audit rows</div>
                <ul className="space-y-1.5" data-testid="nl-audit-links">
                  {detail.audit_links.map((a) => (
                    <li key={a.id} className="text-xs flex items-center gap-2 text-navy-700">
                      <CheckCircle2 className="w-3 h-3 text-success-600" />
                      <span className="font-mono">{a.action}</span>
                      <span className="text-navy-400">{a.at?.slice(0, 19)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="eb-overline mb-1">{label}</div>
      <div className={`text-navy-900 ${mono ? "font-mono text-xs" : ""} break-all`}>{value || "—"}</div>
    </div>
  );
}
